import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";

import { APP_LINKS } from "../lib/constants";
import { buildCustomSchemeUrl, tryOpenAlbaVoxApp } from "../lib/openInApp";

type Props = {
  title?: string;
  description?: string;
  autoOpen?: boolean;
};

export function AppLinkFallback({
  title = "Open this in AlbaVox",
  description = "If the app is installed, this link should open automatically. If it did not, use the button below.",
  autoOpen = true,
}: Props) {
  const location = useLocation();

  const appUrl = useMemo(
    () => buildCustomSchemeUrl(location.pathname, location.search),
    [location.pathname, location.search],
  );

  useEffect(() => {
    if (!autoOpen) return;
    tryOpenAlbaVoxApp(location.pathname, location.search);
  }, [autoOpen, location.pathname, location.search]);

  return (
    <main className="min-h-screen bg-surface px-6 py-16 text-foreground">
      <section className="mx-auto flex max-w-xl flex-col items-center rounded-3xl border border-border-soft bg-surface-elevated p-8 text-center shadow-soft">
        <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-brand-500">
          ALBAVOX
        </p>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          {title}
        </h1>
        <p className="mt-4 text-base leading-7 text-foreground-muted">
          {description}
        </p>

        <a
          href={appUrl}
          className="mt-8 inline-flex rounded-full bg-brand-500 px-6 py-3 font-semibold text-white transition hover:bg-brand-600"
        >
          Open in app
        </a>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href={APP_LINKS.appStore}
            className="rounded-full border border-border-soft px-5 py-3 text-sm font-semibold transition hover:border-brand-500"
          >
            App Store
          </a>
          <a
            href={APP_LINKS.googlePlay}
            className="rounded-full border border-border-soft px-5 py-3 text-sm font-semibold transition hover:border-brand-500"
          >
            Google Play
          </a>
        </div>
      </section>
    </main>
  );
}
